import { useEffect, useState } from 'react';
import { MeasurementSystem } from '@hub/libs/constants';
import { useRouter } from 'next/router';
import { useNextHashQueryParams } from '@hub/hooks/useNextQueryParams';
import { UnitSystem } from '@igus/kopla-domain';
import { UnitSystem as icommerceUnitSystemName } from '@icommerce/units';

const parseUnitSystem = (value: string | string[] | number | undefined, fallback: UnitSystem): UnitSystem => {
  const unitSystemValue = Array.isArray(value) ? value[0] : value;

  if (
    unitSystemValue === UnitSystem.metric ||
    unitSystemValue === UnitSystem.metric.toString() ||
    unitSystemValue === 'metric'
  ) {
    return UnitSystem.metric;
  }

  if (
    unitSystemValue === UnitSystem.imperial ||
    unitSystemValue === UnitSystem.imperial.toString() ||
    unitSystemValue === 'imperial'
  ) {
    return UnitSystem.imperial;
  }

  return fallback;
};

export const useUnitSystem = (initialUnitSystem: UnitSystem) => {
  const [unitSystem, setUnitSystem] = useState<UnitSystem>(initialUnitSystem);
  const router = useRouter();
  const query = useNextHashQueryParams();

  useEffect(() => {
    if (router.isReady) {
      setUnitSystem(parseUnitSystem(query.unitSystem, initialUnitSystem));
    }
  }, [router.isReady]);

  return {
    unitSystem,
    measurementSystem: unitSystem === UnitSystem.metric ? MeasurementSystem.Metric : MeasurementSystem.Imperial,
    setUnitSystem,
  };
};

// Keep compatibility with both legacy numeric values and newer string values. Remove after bearingHubApiVersion v16
export const getLengthCode = (unitSystem: UnitSystem | 'metric' | 'imperial') =>
  unitSystem === UnitSystem.metric || unitSystem === 'metric' ? 'mm' : 'in';
export const getSpeedCode = (unitSystem: UnitSystem | 'metric' | 'imperial') =>
  unitSystem === UnitSystem.metric || unitSystem === 'metric' ? 'm/s' : 'ft/min';
export const getUnitSystemPrefix = (unitSystem: UnitSystem | 'metric' | 'imperial' | 1 | 2) => {
  const unitSystemValue = String(unitSystem);
  const isMetric = unitSystemValue === '1' || unitSystemValue === 'metric';

  return isMetric ? 'm' : 'i';
};
export const getIcommerceUnitSystemName = (unitSystem: UnitSystem | 'metric' | 'imperial') =>
  unitSystem === UnitSystem.metric || unitSystem === 'metric'
    ? icommerceUnitSystemName.metric
    : icommerceUnitSystemName.imperial;
